'use client'
import { Chat as ChatType } from '@/app/lib/types'
import { useChatUpdates } from '@/hooks/useChatUpdates'
import { generateId } from 'ai'
import { differenceInMinutes, formatDistanceStrict } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineX
} from 'react-icons/hi'
import ChatIcon from '../icons/chat-icon'
import ChatIcon34 from '../icons/chat-icon-34'
import { useTheme } from '../lib/themeContext' // Import the theme context
import { Chat } from './chat'

const AnimationSettings = {
  transition: { duration: 0.25, ease: 'easeInOut' },
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 }
}

export function ChatContainer() {
  const { primary, accent, secondary, logo, title } = useTheme() // Consume the theme context
  const [selected, setSelected] = useState<string | null>(null)
  const [chat, setChat] = useChatUpdates(selected)

  const [readOnly, setReadOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [initialInput, setInitialInput] = useState<string | null>(null)
  const [productInfo, setProductInfo] = useState<any>(null) // State to store product info
  const [checkoutMode, setCheckoutMode] = useState(false) // State to track checkout mode
  const [getStartedMode, setGetStartedMode] = useState(false) // State to track getStarted mode
  const searchParams = useSearchParams()
  const router = useRouter() // Add router for navigation
  const pathname = usePathname() // Add pathname for URL construction

  // Capture the url from where the chatbot iframe is loaded
  const entryUrlRef = useRef<string | null>(null)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    entryUrlRef.current = urlParams.get('entryUrl')
  }, [])

  // Create a ref to hold the current chat value
  // We use a ref to avoid re-rendering the component when the chat state changes, for example
  // in the fetchMessages function.
  const chatRef = useRef<ChatType | null>(null)

  const stripHtmlTags = (html: string) => {
    if (typeof document === 'undefined') {
      // Server-side: use regex
      return html
        .replace(/<br\s*\/?>/gi, ' ') // Convert <br> to space
        .replace(/<\/p>/gi, ' ') // Convert </p> to space
        .replace(/<[^>]*>/g, '') // Remove all other HTML tags
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim()
    }

    // Client-side: use DOM
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html.replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ')
    return tmp.textContent?.replace(/\s+/g, ' ').trim() || ''
  }

  // Keep the ref in sync with the state
  useEffect(() => {
    chatRef.current = chat
  }, [chat])

  useEffect(() => {
    // Add a flag to track if this is a first-time load or a back navigation
    const isBackNavigation = localStorage.getItem('isBackNavigation') === 'true'

    // Check for csInventoryId and checkout params
    const csInventoryId = searchParams.get('csInventoryId')
    const isCheckout = searchParams.get('checkout') === 'true'
    const isGetStarted = searchParams.get('getstarted') === 'true'

    if (
      csInventoryId &&
      (isCheckout || isGetStarted) &&
      !selected &&
      !isBackNavigation
    ) {
      // Set checkout mode flag
      if (isCheckout) {
        setCheckoutMode(true)
      }
      if (isGetStarted) {
        setGetStartedMode(true)
      }

      // Fetch product information
      fetch(`/api/products/${csInventoryId}`)
        .then(res => res.json())
        .then(data => {
          setProductInfo(data)
          localStorage.setItem('isProductChat', 'true') // Flag this as a product-specific chat

          // Create a new chat with prefilled messages
          const newChatId = generateId()
          setSelected(newChatId)
        })
        .catch(err => {
          console.error('Failed to fetch product:', err)
          setLoading(false)
        })
      return
    }

    // Handle back navigation or clean state when no search params
    if (
      (!searchParams.has('input') &&
        !searchParams.has('chatId') &&
        !selected) ||
      isBackNavigation
    ) {
      localStorage.removeItem('isBackNavigation')
      setInitialInput(null)
    }

    // Check if there's an input search parameter to start a new chat
    const inputParam = searchParams.get('input')
    if (inputParam && !selected && !isBackNavigation) {
      const newChatId = generateId()
      setSelected(newChatId)
      setInitialInput(inputParam)
    }

    const fetchMessages = () => {
      const storedChatId =
        searchParams.get('chatId') || localStorage.getItem('chatId')
      if (storedChatId) {
        // Only fetch if we don't already have this chat or if it's from URL (for polling)
        if (
          !chatRef.current ||
          chatRef.current.chatId !== storedChatId ||
          searchParams.get('chatId')
        ) {
          fetch(`/api/chat/messages?chatId=${storedChatId}`)
            .then(res => res.json())
            .then(data => {
              setChat(data.chat)

              if (loading) {
                const lastMessageTime = new Date(
                  localStorage.getItem('lastChatDate')!
                )
                const isProductChat =
                  localStorage.getItem('isProductChat') === 'true'
                const isLiveChat = data.chat?.isLiveChat === true

                // Always restore live chats, for regular chats, only restore if less than 5 minutes old
                if (
                  (isLiveChat ||
                    differenceInMinutes(new Date(), lastMessageTime) < 5 ||
                    searchParams.get('chatId')) &&
                  !isProductChat // Do not restore if it was a product chat
                ) {
                  setSelected(storedChatId)
                }
              }
              if (searchParams.get('chatId')) {
                setReadOnly(true)
              }
              setLoading(false)
            })
            .catch(err => {
              console.error('Failed to fetch messages:', err)
              setLoading(false)
            })
        } else {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    fetchMessages()

    const storedChatId =
      searchParams.get('chatId') || localStorage.getItem('chatId')
    if (storedChatId) {
      const interval = setInterval(fetchMessages, 5000)

      return () => clearInterval(interval)
    }
  }, [selected, searchParams, loading, pathname, router])

  // Only use saved messages when resuming the same chat, not when starting a new one
  const messages =
    chat?.messages && chat.chatId === selected ? chat.messages : undefined

  const handleClose = () => {
    window.parent.postMessage({ type: 'closeIframe' }, '*')
  }

  const handleBack = () => {
    // Set flag to indicate we're navigating back
    localStorage.setItem('isBackNavigation', 'true')

    setSelected(null)
    setInitialInput(null)
    setCheckoutMode(false) // Reset checkout mode
    setGetStartedMode(false)
    setProductInfo(null) // Reset product info
    // Force clear the readOnly flag to ensure home screen shows properly
    setReadOnly(false)

    // Clear URL search parameters completely
    if (window) {
      // Use window.history to clear URL parameters without triggering a page reload
      const newUrl = window.location.pathname
      window.history.pushState({}, '', newUrl)
    }

    // Replace current URL without any search params
    router.replace(pathname, { scroll: false })
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <AnimatePresence initial={false} mode="sync">
        {selected ? (
          <motion.div
            className="absolute flex h-screen w-full flex-col items-center justify-center"
            key="chat"
            {...AnimationSettings}
          >
            <div className="flex size-full flex-1 flex-col overflow-hidden">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#D9D9D9] bg-white px-4 py-2.5 shadow-md">
                <div className="flex items-center gap-3.5">
                  {!readOnly && (
                    <button onClick={handleBack}>
                      <HiOutlineChevronLeft className="size-5" />
                    </button>
                  )}
                  <div className="flex items-center gap-0.5">
                    <div>
                      <ChatIcon fill={accent} />
                    </div>
                    <div className="font-bold" style={{ color: secondary }}>
                      {title}
                    </div>
                  </div>
                </div>
                {!readOnly && (
                  <div>
                    <button onClick={handleClose}>
                      <HiOutlineX className="h-6 w-6" />
                    </button>
                  </div>
                )}
              </div>
              <Chat
                chat={chat}
                readOnly={readOnly}
                id={selected}
                // @ts-ignore TODO: fix this
                savedMessages={messages}
                initialInput={initialInput}
                initialCurrentPrompts={
                  chat && chat.chatId === selected
                    ? JSON.parse(
                        localStorage.getItem(`currentPrompts-${selected}`)!
                      )
                    : undefined
                }
                initialShowFeedback={
                  chat && chat.chatId === selected
                    ? JSON.parse(
                        localStorage.getItem(`showFeedback-${selected}`)!
                      )
                    : undefined
                }
                initialWasPromptClicked={
                  chat && chat.chatId === selected
                    ? JSON.parse(
                        localStorage.getItem(`promptClicked-${selected}`)!
                      )
                    : undefined
                }
                productInfo={productInfo}
                checkoutMode={checkoutMode}
                getStartedMode={getStartedMode}
                entryUrl={entryUrlRef.current}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="absolute flex h-screen w-full flex-col items-center justify-center"
            key={'home'}
            {...AnimationSettings}
          >
            <div className="flex w-full flex-1 flex-col overflow-hidden">
              <div
                className="flex justify-between px-4 py-3 shadow-md"
                style={{ backgroundColor: primary }}
              >
                {logo}
                <button onClick={handleClose}>
                  <HiOutlineX
                    className={`h-6 w-6 ${
                      primary === '#ffffff' ? 'text-black' : 'text-white'
                    }`}
                  />
                </button>
              </div>
              <div className="flex flex-1 flex-col gap-11 bg-[linear-gradient(180deg,_#FFF_0%,_#E8E8E8_82.07%)] px-4 pt-10">
                <div className="flex flex-col gap-0.5">
                  <div
                    className="text-3xl font-bold"
                    style={{ color: `${accent}C9` }}
                  >
                    Hi There 👋
                  </div>
                  <div className="text-3xl font-bold" style={{ color: accent }}>
                    How can we help?
                  </div>
                </div>
                <div className="flex flex-col gap-5">
                  <button
                    onClick={() => {
                      const id = generateId()
                      // Clear localStorage data for a truly fresh start
                      localStorage.removeItem('currentPrompts')
                      localStorage.removeItem('showFeedback')
                      localStorage.removeItem('promptClicked')
                      localStorage.removeItem('isProductChat')
                      return setSelected(id)
                    }}
                    className="rounded-lg border px-2.5 py-4 text-left shadow-md"
                  >
                    <div className="flex gap-3">
                      <div>
                        <ChatIcon34 fill={accent} />
                      </div>
                      <div className="flex flex-1 flex-col gap-2 overflow-hidden">
                        <div className="font-bold" style={{ color: secondary }}>
                          Start New Chat
                        </div>
                        <div className="text-xs">
                          Let us help you find what you&apos;re looking for.
                        </div>
                      </div>
                      <div className="self-center">
                        <HiOutlineChevronRight
                          className="h-5 w-5"
                          style={{
                            color: primary === '#ffffff' ? '#808080' : primary
                          }}
                        />
                      </div>
                    </div>
                  </button>
                  {chat && localStorage.getItem('isProductChat') !== 'true' && (
                    <button
                      onClick={() => {
                        return setSelected(chat.chatId)
                      }}
                      className="rounded-lg border px-2.5 py-4 text-left shadow-md"
                    >
                      <div className="flex gap-3">
                        <div>
                          <ChatIcon34 fill={accent} />
                        </div>
                        <div className="flex flex-1 flex-col gap-2 overflow-hidden">
                          <div
                            className="font-bold"
                            style={{ color: secondary }}
                          >
                            Resume Chat
                          </div>
                          <div className="line-clamp-1 text-xs">
                            {stripHtmlTags(
                              localStorage.getItem('lastChatMessage') || ''
                            )}
                            {}
                          </div>
                          <div className="text-xs" style={{ color: secondary }}>
                            {formatDistanceStrict(
                              new Date(),
                              new Date(localStorage.getItem('lastChatDate')!)
                            )}{' '}
                            ago
                          </div>
                        </div>
                        <div className="self-center">
                          <HiOutlineChevronRight
                            className="h-5 w-5"
                            style={{
                              color: primary === '#ffffff' ? '#808080' : primary
                            }}
                          />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
